import { api } from '@/services/api';
import { AgentsFilterLocal } from '@/pages/agents/Agents/agents-query-reducer';
import {
  AdminAgentsResponseDto,
  GetAdminAgentsDto,
  CreateAgentDealDto,
  UpdateAgentDealDto,
  CreateAgentDealUpdateRequestDto,
  UpdateAgentDealUpdateRequestDto,
  AgentDealUpdateRequest,
  GetAdminAgentDealUpdateRequestsDto,
  AdminAgentDealUpdateRequestsResponseDto,
  AdminAgentsFilterDto,
  AdminAgentDirectReferralsResponseDto,
  AgentAssociationMigrationBatchDetailDto,
  AgentAssociationMigrationBatchListResponseDto,
  CreateAgentAssociationMigrationDto,
  CreateAgentAssociationMigrationResponseDto,
  GetAdminAgentDirectReferralsDto,
  GetAgentAssociationMigrationBatchesDto,
  MigrationVerificationDto,
  SearchUsersByAliasesResponseDto,
} from '@repo/common-types';
import { processApiError } from '@repo/utils';
import { AgentDealUpdateRequestsFilterLocal } from '@/pages/agents/AgentDealUpdateRequests/agent-deal-update-requests-query-reducer';
import { downloadBlobAsFile } from '@/common/utils/download-file';

/**
 * Service for managing Agent Deals and related operations
 * Based on the backend API endpoints from the requirements document
 */

/**
 * Convert local filter format to API format
 */
export const convertFilterToApiFormat = (localFilter?: AgentsFilterLocal): GetAdminAgentsDto['filter'] => {
  const apiFilter: GetAdminAgentsDto['filter'] = {};

  if (localFilter?.search) apiFilter.search = localFilter.search;
  if (localFilter?.dealType) apiFilter.dealType = localFilter.dealType;
  if (localFilter?.userAgreementType) apiFilter.userAgreementType = localFilter.userAgreementType;
  if (localFilter?.dealStatus) {
    if (Array.isArray(localFilter.dealStatus) && localFilter.dealStatus.length > 0) {
      apiFilter.dealStatus = localFilter.dealStatus;
    } else if (!Array.isArray(localFilter.dealStatus)) {
      apiFilter.dealStatus = [localFilter.dealStatus];
    }
  }
  if (localFilter?.alias) apiFilter.alias = localFilter.alias;
  if (localFilter?.email) apiFilter.email = localFilter.email;
  // Explicitly check for boolean type to include false values
  if (typeof localFilter?.isRegionalManager === 'boolean') {
    apiFilter.isRegionalManager = localFilter.isRegionalManager;
  }

  return apiFilter;
};

export const convertAgentDealUpdateRequestsFilterToApiFormat = (
  localFilter?: AgentDealUpdateRequestsFilterLocal,
): GetAdminAgentDealUpdateRequestsDto['filter'] => {
  const apiFilter: GetAdminAgentDealUpdateRequestsDto['filter'] = {};

  if (localFilter?.search) apiFilter.search = localFilter.search;

  // Handle boolean for applied status
  if (typeof localFilter?.applied === 'boolean') {
    apiFilter.applied = localFilter.applied;
  }

  return apiFilter;
};

/**
 * Fetch all agents (users with isAgent = true) with their associated AgentDeals
 */
export const fetchAgentsRest = async (params: GetAdminAgentsDto): Promise<AdminAgentsResponseDto> => {
  try {
    const response = await api.get('/v1/admin/agent-deals/agents', { params });
    return response.data;
  } catch (error) {
    const userFriendlyMessage = processApiError(error);
    throw new Error(userFriendlyMessage);
  }
};

/**
 * Fetch a specific agent by ID with full details
 */
export const fetchAgentByIdRest = async (agentId: string) => {
  const response = await api.get(`/v1/admin/agent-deals/agents/${agentId}`);
  return response.data;
};

/**
 * Create a new agent deal for a user
 */
export const createAgentDealRest = async (agentDealData: CreateAgentDealDto) => {
  try {
    const response = await api.post(`/v1/admin/agent-deals/deals`, agentDealData);
    return response.data;
  } catch (error) {
    const userFriendlyMessage = processApiError(error);
    throw new Error(userFriendlyMessage);
  }
};

/**
 * Update an existing agent deal
 */
export const updateAgentDealRest = async (agentDealId: string, agentDealData: UpdateAgentDealDto) => {
  try {
    const response = await api.put(`/v1/admin/agent-deals/deals/${agentDealId}`, agentDealData);
    return response.data;
  } catch (error) {
    const userFriendlyMessage = processApiError(error);
    throw new Error(userFriendlyMessage);
  }
};

/**
 * Deactivate an agent deal (soft delete)
 */
export const deactivateAgentDealRest = async (agentDealId: string) => {
  try {
    const response = await api.delete(`/v1/admin/agent-deals/deals/${agentDealId}`);
    return response.data;
  } catch (error) {
    const userFriendlyMessage = processApiError(error);
    throw new Error(userFriendlyMessage);
  }
};

/**
 * Get agent deal history for a user
 */
export const fetchAgentDealsByUserIdRest = async (
  userId: string,
  params: {
    skip: number;
    take: number;
    filter?: any;
    sorts?: Array<{ field: string; order: any }>;
  },
) => {
  try {
    const response = await api.get(`/v1/admin/agent-deals/users/${userId}/deals`, { params });
    return response.data;
  } catch (error) {
    const userFriendlyMessage = processApiError(error);
    throw new Error(userFriendlyMessage);
  }
};

/**
 * Get active agent deal for a user
 */
export const fetchActiveAgentDealByUserIdRest = async (userId: string) => {
  try {
    const response = await fetchAgentDealsByUserIdRest(userId, {
      skip: 0,
      take: 1,
      filter: { dealStatus: 'Active' },
      sorts: [{ field: 'createdAt', order: 'desc' }],
    });

    if (response.items && response.items.length > 0) {
      return response.items[0];
    }

    return null;
  } catch (error) {
    const userFriendlyMessage = processApiError(error);
    throw new Error(userFriendlyMessage);
  }
};

export const fetchAgentDirectReferralsRest = async (
  agentId: string,
  params: GetAdminAgentDirectReferralsDto,
): Promise<AdminAgentDirectReferralsResponseDto> => {
  try {
    const response = await api.get(`/v1/admin/agent-deals/agents/${agentId}/direct-referrals`, { params });
    return response.data;
  } catch (error) {
    const userFriendlyMessage = processApiError(error);
    throw new Error(userFriendlyMessage);
  }
};

export const fetchAgentDirectReferralIdsRest = async (
  agentId: string,
  params: { search?: string },
): Promise<string[]> => {
  try {
    const response = await api.get(`/v1/admin/agent-deals/agents/${agentId}/direct-referrals/ids`, { params });
    return response.data;
  } catch (error) {
    const userFriendlyMessage = processApiError(error);
    throw new Error(userFriendlyMessage);
  }
};

export const fetchUnassignedPlayersRest = async (
  params: GetAdminAgentDirectReferralsDto,
): Promise<AdminAgentDirectReferralsResponseDto> => {
  try {
    const response = await api.get(`/v1/admin/agent-deals/unassigned-players`, { params });
    return response.data;
  } catch (error) {
    const userFriendlyMessage = processApiError(error);
    throw new Error(userFriendlyMessage);
  }
};

export const fetchUnassignedPlayerIdsRest = async (params: {
  search?: string;
  countryCodes?: string[];
}): Promise<string[]> => {
  try {
    const response = await api.get(`/v1/admin/agent-deals/unassigned-players/ids`, { params });
    return response.data;
  } catch (error) {
    const userFriendlyMessage = processApiError(error);
    throw new Error(userFriendlyMessage);
  }
};

export const createAgentAssociationMigrationRest = async (
  dto: CreateAgentAssociationMigrationDto,
): Promise<CreateAgentAssociationMigrationResponseDto> => {
  try {
    const response = await api.post(`/v1/admin/agent-deals/agent-association-migrations`, dto);
    return response.data;
  } catch (error) {
    const userFriendlyMessage = processApiError(error);
    throw new Error(userFriendlyMessage);
  }
};

export const fetchAgentAssociationMigrationBatchesRest = async (
  params: GetAgentAssociationMigrationBatchesDto,
): Promise<AgentAssociationMigrationBatchListResponseDto> => {
  try {
    const response = await api.get(`/v1/admin/agent-deals/agent-association-migrations`, { params });
    return response.data;
  } catch (error) {
    const userFriendlyMessage = processApiError(error);
    throw new Error(userFriendlyMessage);
  }
};

export const fetchAgentAssociationMigrationBatchRest = async (
  batchId: string,
): Promise<AgentAssociationMigrationBatchDetailDto> => {
  try {
    const response = await api.get(`/v1/admin/agent-deals/agent-association-migrations/${batchId}`);
    return response.data;
  } catch (error) {
    const userFriendlyMessage = processApiError(error);
    throw new Error(userFriendlyMessage);
  }
};

export const downloadAgentAssociationMigrationCsvRest = async (batchId: string): Promise<void> => {
  const response = await api.get(`/v1/admin/agent-deals/agent-association-migrations/${batchId}/export`, {
    responseType: 'blob',
  });

  downloadBlobAsFile({
    blob: new Blob([response.data], { type: 'text/csv' }),
    filename: `agent_association_migration_${batchId}.csv`,
  });
};

export const fetchMigrationVerificationRest = async (batchId: string): Promise<MigrationVerificationDto> => {
  try {
    const response = await api.get(`/v1/admin/agent-deals/agent-association-migrations/${batchId}/verification`);
    return response.data;
  } catch (error) {
    const userFriendlyMessage = processApiError(error);
    throw new Error(userFriendlyMessage);
  }
};

export const searchUsersByAliasesRest = async (aliases: string[]): Promise<SearchUsersByAliasesResponseDto> => {
  try {
    const response = await api.post(`/v1/admin/agent-deals/search-users-by-aliases`, { aliases });
    return response.data;
  } catch (error) {
    const userFriendlyMessage = processApiError(error);
    throw new Error(userFriendlyMessage);
  }
};

export const processMigrationBatchRest = async (batchId: string): Promise<{ status: string | null }> => {
  try {
    const response = await api.post(`/v1/admin/agent-deals/agent-association-migrations/${batchId}/process`);
    return response.data;
  } catch (error) {
    const userFriendlyMessage = processApiError(error);
    throw new Error(userFriendlyMessage);
  }
};

/**
 * Fetch rake share updates for agents
 */
export const fetchAgentDealUpdateRequestsRest = async (
  params: GetAdminAgentDealUpdateRequestsDto,
): Promise<AdminAgentDealUpdateRequestsResponseDto> => {
  try {
    const response = await api.get(`/v1/admin/agent-deals/agent-deal-update-requests`, { params });
    return response.data;
  } catch (error) {
    const userFriendlyMessage = processApiError(error);
    throw new Error(userFriendlyMessage);
  }
};

/**
 * Create a rake share update (admin)
 */
export const createAgentDealUpdateRequestRest = async (
  dto: CreateAgentDealUpdateRequestDto,
): Promise<AgentDealUpdateRequest> => {
  try {
    const response = await api.post(`/v1/admin/agent-deals/agent-deal-update-requests`, dto);
    return response.data;
  } catch (error) {
    const userFriendlyMessage = processApiError(error);
    throw new Error(userFriendlyMessage);
  }
};

/**
 * Update a pending rake share update (admin)
 */
export const updateAgentDealUpdateRequestRest = async (
  id: string,
  dto: UpdateAgentDealUpdateRequestDto,
): Promise<AgentDealUpdateRequest> => {
  try {
    const response = await api.put(`/v1/admin/agent-deals/agent-deal-update-requests/${id}`, dto);
    return response.data;
  } catch (error) {
    const userFriendlyMessage = processApiError(error);
    throw new Error(userFriendlyMessage);
  }
};

/**
 * Apply a pending rake share update (admin)
 */
export const applyAgentDealUpdateRequestRest = async (id: string): Promise<AgentDealUpdateRequest> => {
  try {
    const response = await api.put(`/v1/admin/agent-deals/agent-deal-update-requests/${id}/apply`);
    return response.data;
  } catch (error) {
    const userFriendlyMessage = processApiError(error);
    throw new Error(userFriendlyMessage);
  }
};

/**
 * Delete a pending rake share update (admin)
 */
export const deleteAgentDealUpdateRequestRest = async (id: string): Promise<AgentDealUpdateRequest> => {
  try {
    const response = await api.delete(`/v1/admin/agent-deals/agent-deal-update-requests/${id}`);
    return response.data;
  } catch (error) {
    const userFriendlyMessage = processApiError(error);
    throw new Error(userFriendlyMessage);
  }
};
